/**
 * Video Analytics API utilities
 * This file provides functions for interacting with the video analysis backend.
 * All analysis is performed via real Gemini API calls - no mock/demo fallbacks.
 */
import axios from 'axios';
import apiClient from './apiClient';
import config from './config';
import { createDummyMP4FileObject, detectIOSVideo, getAuthToken, getPlayerIdFromUrl, getSelectedSport, getUser, message, submitVideoAnalysis, updateVideoAnalysis, uploadFileToGCS } from './helpers';
import { goToLogin } from './navigation';

// Base URL for API endpoints
const COACH_API_BASE_URL = config.COACH_API_BASE_URL;
const VIDEO_ANALYTICS_BASE_URL = config.API_BASE_URL;
// console.log('- Using Coach API base URL:', COACH_API_BASE_URL);
// console.log('- Using Video Analytics API base URL:', VIDEO_ANALYTICS_BASE_URL);

// Google Cloud Gemini API configuration
// console.log('- REACT_APP_GEMINI_LOCATION directly:', process.env.REACT_APP_GEMINI_LOCATION);
// console.log('- REACT_APP_GEMINI_PROJECT_ID directly:', process.env.REACT_APP_GEMINI_PROJECT_ID);
// console.log('- REACT_APP_GEMINI_MODEL directly:', process.env.REACT_APP_GEMINI_MODEL);

const GEMINI_LOCATION = process.env.REACT_APP_GEMINI_LOCATION || 'us-central1';
const GEMINI_PROJECT_ID = process.env.REACT_APP_GEMINI_PROJECT_ID || 'disearch'; // Hardcoded as fallback
// Default: gemini-2.5-flash (stable, cost-effective, excellent multimodal) - Updated Jan 2026
const GEMINI_MODEL = process.env.REACT_APP_GEMINI_MODEL || 'gemini-2.5-flash';
// console.log('- Gemini API config:', GEMINI_LOCATION, GEMINI_PROJECT_ID, GEMINI_MODEL);
// console.log('- Google API Key available:', process.env.REACT_APP_GOOGLE_API_KEY ? 'Yes' : 'No');

/**
 * Analyze a video file or YouTube URL
 * @param {Object} options - Analysis options
 * @param {File} options.videoFile - Video file to analyze (optional)
 * @param {string} options.youtubeUrl - YouTube URL to analyze (optional)
 * @param {string} options.instructions - Additional analysis instructions
 * @param {string} options.csvId - CSV ID for video-csv analysis (optional)
 * @param {string} options.csvName - CSV name for video-csv analysis (optional)
 * @returns {Promise<string>} Analysis result as markdown text
 */
export const analyzeVideo = async ({ videoFile, youtubeUrl, instructions = '', player_id = null, folderId = null, csvId = null }) => {
  try {
    let analysisTitle = ''
    // Log environment for debugging
    console.log('Video Analysis - Environment:');
    console.log('- NODE_ENV:', process.env.NODE_ENV);
    console.log('- COACH_API_BASE_URL:', COACH_API_BASE_URL);
    console.log('- VIDEO_ANALYTICS_BASE_URL:', VIDEO_ANALYTICS_BASE_URL);

    // PRODUCTION MODE: All analysis uses real Gemini API
    console.log('Using real Gemini API for video analysis');

    try {
      console.log('Attempting to use real API calls...');

      // Process YouTube URLs with Gemini API
      if (youtubeUrl) {
        console.log('YouTube URL detected, using Vertex AI Gemini API:', youtubeUrl);

        // Check if we have the minimum required configuration
        const hasProjectConfig = GEMINI_PROJECT_ID !== 'your-project-id';

        if (!hasProjectConfig) {
          console.error('Gemini API not configured: Missing GEMINI_PROJECT_ID');
          throw new Error('Video analysis is not configured. Please contact support to enable Gemini API.');
        }

        try {
          // Prepare the analysis prompt
          const analysisPrompt = instructions
            ? `Analyze this video and provide detailed feedback. ${instructions}. Format your response in markdown with sections for Technical Breakdown, Pattern Recognition, Uniqueness and Individuality, Development Plan, and Mental Edge. Include specific, actionable feedback.`
            : `Analyze this video and provide detailed feedback. Focus on technique, positioning, and areas for improvement. Format your response in markdown with sections for Technical Breakdown, Pattern Recognition, Uniqueness and Individuality, Development Plan, and Mental Edge. Include specific, actionable feedback.`;

          console.log('Creating video analysis record for YouTube URL');

          const user = getUser()

          // For YouTube URLs, we don't upload a dummy file anymore.
          // The backend (slam-agents) will download the YouTube video and upload it to GCS.
          // We just need to create the database record and trigger analysis.
          const submitPayload = {
            player_id: player_id ? player_id : (getPlayerIdFromUrl() || ''),
            response: '',
            user_id: user?.id,
            video_name: `youtube-${Date.now()}.mp4`,  // Placeholder name, will be updated by backend
            folderId: folderId,
            title: analysisTitle,
            sport: getSelectedSport(),
            size: 0,  // Size unknown until backend downloads
            source_type: 'youtube',
            source_url: youtubeUrl,
            ingestion_method: 'yt-dlp'
          }

          const analysisResult = await submitVideoAnalysis(submitPayload)
          console.log('analysisResult', analysisResult)
          // submitVideoAnalysis returns { message, data: { id, ... } } on success
          // or { error, success: false } on failure
          const recordId = analysisResult?.data?.id || analysisResult?.id;
          if (!recordId) {
            const errorMsg = analysisResult?.error || 'Uploading failed please try again later';
            throw new Error(errorMsg);
          }

          // Update video record with YouTube URL and trigger analysis
          // The backend will:
          // 1. Download the YouTube video
          // 2. Upload it to GCS with consistent path (videos/{timestamp}-{video_id}.mp4)
          // 3. Update the gs_url in the database with the actual GCS URI
          // 4. Analyze the video with Gemini
          const updateResult = await updateVideoAnalysis({
            id: recordId,
            gs_uri: youtubeUrl,  // Backend will replace this with actual GCS URI after download
            csv_id: csvId,
            source_type: 'youtube',
            source_url: youtubeUrl,
            ingestion_method: 'yt-dlp'
          })

          // Backend returns analysis_result when analysis completes synchronously (local dev)
          if (updateResult?.analysis_result) {
            return updateResult.analysis_result;
          }

          // For async (production Pub/Sub), return a status message
          return 'Video submitted for analysis. The analysis is being processed and will appear in your video list when complete.';

        } catch (vertexError) {
          console.error('Vertex AI error:', vertexError);

          // If it's a specific error we want to show to the user
          if (vertexError.message.includes('YouTube') ||
            vertexError.message.includes('permission') ||
            vertexError.message.includes('unavailable') ||
            vertexError.message.includes('quota')) {
            throw vertexError;
          }

          // Re-throw the error - no mock fallbacks in production
          console.error('Vertex AI error - re-throwing to show user the actual error');
          throw vertexError;
        }
      }

      // For regular video file uploads, use Vertex AI approach
      else if (videoFile) {
        console.log('Video file detected, preparing for upload with Vertex AI:', videoFile.name, videoFile.type, videoFile.size);

        // Detect iOS video formats (HEVC/MOV) — server will auto-transcode to H.264/MP4
        const iosCheck = detectIOSVideo(videoFile);
        if (iosCheck.isIOS) {
          console.log(`iOS video detected: ${iosCheck.reason}. Server will transcode to H.264/MP4.`);
          message.info('iOS video detected — optimizing for analysis. This may take a moment.', 6000);
        }

        console.log('videoFile', videoFile)

        try {
          // Prepare the analysis prompt
          const analysisPrompt = instructions
            ? `${instructions}`
            : ``;
          const user = getUser()

          const submitPayload = {
            player_id: player_id ? player_id : (getPlayerIdFromUrl() || ''),
            response: '',
            user_id: user?.id,
            video_name: videoFile?.name,
            title: analysisTitle,
            sport: getSelectedSport(),
            folderId: folderId,
            size: videoFile?.size || 0,
            csv_id: csvId || '',
            source_type: 'gcs',
            source_url: '',
            ingestion_method: 'direct_upload',
          }

          const analysisId = await submitVideoAnalysis(submitPayload)
          console.log('analysisId', analysisId)
          if (!analysisId?.id) {
            // Handle structured error response from submitVideoAnalysis
            const errorMsg = analysisId?.error || 'Uploading failed please try again later';
            throw new Error(errorMsg);
          }
          const metadata = {
            Sports: getSelectedSport(),
            PlayerID: player_id ? player_id : (getPlayerIdFromUrl() || ''),
            UserID: user?.id,
            Query: analysisPrompt,
            AnalysisTitle: analysisTitle,
            VideoID: analysisId?.id,
            is_analysis: true,
            csv_id: csvId || '',
            ...(iosCheck.isIOS ? { needs_transcoding: 'true', ios_format: iosCheck.reason } : {}),
          }

          const getGSURL = await uploadFileToGCS(videoFile, metadata);

          await updateVideoAnalysis({
            id: analysisId?.id,
            gs_uri: getGSURL,
            csv_id: csvId,
          })
          console.log('Creating form data with video file for upload');

          return true
        } catch (fetchError) {
          // Handle file upload errors
          console.error('File upload error:', fetchError);
          throw fetchError;
        }
      } else {
        throw new Error('Either videoFile or youtubeUrl must be provided');
      }
    } catch (apiError) {
      // Handle API errors
      console.log('API error details:', apiError);

      // All errors are re-thrown to show user the actual error
      console.error('Video analysis API error:', apiError.message);
      throw apiError;
    }
  } catch (error) {
    // Handle any other errors
    console.error('Error in video analysis:', error);
    throw error;
  }
};

/**
 * Generate audio narration from analysis text using Gemini
 * @param {string} text - The text to convert to speech
 * @param {string} voice - Voice type ('male' or 'female')
 * @returns {Promise<Object>} Object containing URL to the audio file and narration text
 */
export const generateAudio = async (text, voice) => {
  try {
    console.log('Audio generation requested for', voice, 'voice using Gemini');

    // PRODUCTION MODE: No mock fallbacks - always use real API
    console.log('Production mode: Using real API for audio generation');

    try {
      console.log('Attempting real API call for audio generation with Gemini');

      // Create AbortController for the fetch timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        console.log('Audio generation request timed out');
        controller.abort();
      }, 60000); // 60 second timeout for audio generation (increased for Gemini processing)

      // Test server connection first
      console.log('Testing server connection before audio request');
      // const testResponse = await fetch(`${COACH_API_BASE_URL}`, {
      //   method: 'HEAD',
      // Test server connection before making the request
      try {
        const response = await axios.head(COACH_API_BASE_URL, {
          headers: {
            Authorization: getAuthToken(),
          },
          signal: controller.signal,  // Axios supports AbortController signals from v1.2+
        });

        // If axios HEAD succeeds, response is available - server is reachable
        if (!response) {
          console.error('Cannot reach audio generation server');
          clearTimeout(timeoutId);
          throw new Error('Audio generation server is not available. Please try again later.');
        }
      } catch (error) {
        if (!error?.response && error?.code === 'ERR_NETWORK') {
          goToLogin(); // ✅ Redirect to login on network error
        }

        console.error('Server connection test failed:', error.message);
        clearTimeout(timeoutId);
        throw new Error('Unable to connect to audio generation service. Please check your connection.');
      }

      console.log('Making text-to-speech API request to:', `${COACH_API_BASE_URL}/text-to-speech`);

      // Make API request
      const response = await fetch(`${COACH_API_BASE_URL}/text-to-speech`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text,
          voice,
        }),
        signal: controller.signal
      });

      clearTimeout(timeoutId); // Clear the timeout

      console.log('Audio generation API response received, status:', response.status);

      if (!response.ok) {
        const errorData = await response.json().catch(() => {
          console.log('Could not parse audio error response as JSON');
          return null;
        });
        console.log('Audio error response data:', errorData);
        throw new Error(errorData?.message || `Server error: ${response.status}`);
      }

      const data = await response.json();
      console.log('Audio successfully generated, URL:', data.audioUrl);
      console.log('Narration text received:', data.narrationText ? 'Yes' : 'No');

      return {
        audioUrl: data.audioUrl,
        narrationText: data.narrationText || null
      };
    } catch (fetchError) {
      console.error('Audio API error:', fetchError);
      // Re-throw the error - no mock fallbacks in production
      throw fetchError;
    }
  } catch (error) {
    console.error('Error generating audio:', error);
    throw error;
  }
};

/**
 * Generate PDF report from analysis text
 * @param {string} text - The markdown text to convert to PDF
 * @returns {Promise<string>} URL to the generated PDF file
 */
export const generatePDF = async (text) => {
  try {
    console.log('PDF generation requested');

    // PRODUCTION MODE: No mock fallbacks - always use real API
    console.log('Production mode: Using real API for PDF generation');

    try {
      console.log('Attempting real API call for PDF generation');

      // Create AbortController for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        console.log('PDF generation request timed out');
        controller.abort();
      }, 15000); // 15 seconds

      console.log('Making PDF generation API request to:', `${COACH_API_BASE_URL}/generate-pdf`);

      const response = await axios.post(
        `${COACH_API_BASE_URL}/generate-pdf`,
        { text },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: getAuthToken(),
          },
          signal: controller.signal,
        }
      );

      clearTimeout(timeoutId);

      console.log('PDF generation API response received, status:', response.status);

      return response.data.pdfUrl;

    } catch (error) {
      if (axios.isCancel(error)) {
        console.error('PDF generation request timed out');
        throw new Error('PDF generation timed out. Please try again.');
      } else if (!error?.response && error?.code === 'ERR_NETWORK') {
        goToLogin(); // ✅ Redirect to login on network error
      } else {
        console.error('PDF API error:', error);
      }

      // Re-throw the error - no mock fallbacks in production
      throw error;
    }
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw error;
  }
};

// NOTE: generateMockAnalysis function has been REMOVED
// All video analysis now uses real Gemini API - no fallback/demo mode
// If you need to test without API, use proper test mocking in Jest

/**
 * Get all saved video analytics
 * @returns {Promise<Array>} List of all saved video analytics
 */
export const getAllVideoAnalytics = async () => {
  try {
    const response = await axios.get(`${VIDEO_ANALYTICS_BASE_URL}/video-analytics`, {
      headers: {
        Authorization: getAuthToken(),
      },
    });

    return response.data;
  } catch (error) {
    if (!error?.response && error?.code === 'ERR_NETWORK') {
      goToLogin(); // ✅ Redirect to login on network error
    } else {
      console.error('Error getting all video analytics:', error);
    }
    throw error;
  }
};

/**
 * Get video analytics by ID
 * @param {string} id - The ID of the analytics to retrieve
 * @returns {Promise<Object>} The video analytics data
 */
export const getVideoAnalyticsById = async (id) => {
  try {
    const response = await axios.get(`${VIDEO_ANALYTICS_BASE_URL}/video-analytics/${id}`, {
      headers: {
        Authorization: getAuthToken(),
      },
    });

    return response.data;
  } catch (error) {
    if (!error?.response && error?.code === 'ERR_NETWORK') {
      goToLogin(); // ✅ Redirect to login on network error
    } else {
      console.error('Error getting video analytics by ID:', error);
    }
    throw error;
  }
};

/**
 * Get video analytics by Roster ID
 * @param {string} playerId - The Roster ID to find analytics for
 * @returns {Promise<Array>} List of video analytics for the Roster
 */
export const getVideoAnalyticsByPlayerId = async (playerId) => {
  try {
    const response = await axios.get(`${VIDEO_ANALYTICS_BASE_URL}/rosters/${playerId}/video-analytics`, {
      headers: {
        Authorization: getAuthToken(),
      },
    });

    return response.data;
  } catch (error) {
    if (!error?.response && error?.code === 'ERR_NETWORK') {
      goToLogin(); // ✅ Redirect to login on network error
    } else {
      console.error('Error getting video analytics by Roster ID:', error);
    }
    throw error;
  }
};

/**
 * Save video analytics
 * @param {Object} data - The analytics data to save
 * @returns {Promise<Object>} The saved analytics with ID
 */
export const saveVideoAnalytics = async (data) => {
  try {
    const response = await axios.post(`${VIDEO_ANALYTICS_BASE_URL}/video-analytics`, data, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: getAuthToken(),
      },
    });

    return response.data;
  } catch (error) {
    if (!error?.response && error?.code === 'ERR_NETWORK') {
      goToLogin(); // ✅ Redirect to login on network error
    } else {
      console.error('Error saving video analytics:', error);
    }
    throw error;
  }
}

/**
 * Update video analytics
 * @param {string} id - The ID of the analytics to update
 * @param {Object} data - The updated analytics data
 * @returns {Promise<Object>} The updated analytics
 */
export const updateVideoAnalytics = async (id, data) => {
  try {
    const response = await axios.put(`${VIDEO_ANALYTICS_BASE_URL}/video-analytics/${id}`, data, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: getAuthToken(),
      },
    });

    return response.data;
  } catch (error) {
    if (!error?.response && error?.code === 'ERR_NETWORK') {
      goToLogin(); // ✅ Redirect to login on network error
    } else {
      console.error('Error updating video analytics:', error);
    }
    throw error;
  }
};

/**
 * Delete video analytics
 * @param {string} id - The ID of the analytics to delete
 * @returns {Promise<Object>} Success message
 */
export const deleteVideoAnalytics = async (id) => {
  try {
    const response = await axios.delete(`${VIDEO_ANALYTICS_BASE_URL}/video-analytics/${id}`, {
      headers: {
        Authorization: getAuthToken(),
      },
    });

    return response.data;
  } catch (error) {
    if (!error?.response && error?.code === 'ERR_NETWORK') {
      goToLogin(); // ✅ Redirect to login on network error
    } else {
      console.error('Error deleting video analytics:', error);
    }
    throw error;
  }
};

/**
 * Analyze multiple video files in parallel
 * @param {Object} options - Analysis options
 * @param {File[]} options.videoFiles - Array of video files to analyze
 * @param {string} options.instructions - Additional analysis instructions
 * @returns {Promise<Array<string>>} Array of analysis results as markdown text
 */
export const analyzeMultipleVideos = async ({ videoFiles, instructions = '', player_id = null, folderId = null, csvId = null }) => {
  try {
    let analysisTitle = ''
    console.log('Multiple video analysis requested:', videoFiles.length, 'files');

    // PRODUCTION MODE: No mock fallbacks - always use real Gemini API
    console.log('Production mode: Using real Gemini API for multiple video analysis');

    // Process each video file in parallel
    const analysisPromises = videoFiles.map(async (videoFile) => {
      const controller = new AbortController();
      const timeoutDuration = 300000; // 5 minutes per video
      const timeoutId = setTimeout(() => {
        console.log(`Request timed out for ${videoFile.name} after ${timeoutDuration / 1000} seconds`);
        controller.abort();
      }, timeoutDuration);

      try {
        const user = getUser()

        const analysisPrompt = instructions
          ? `${instructions}`
          : ``;

        const submitPayload = {
          player_id: player_id ? player_id : (getPlayerIdFromUrl() || ''),
          response: '',
          user_id: user?.id,
          sport: getSelectedSport(),
          size: videoFile?.size || 0,
          video_name: videoFile?.name,
          folderId: folderId,
          title: analysisTitle,
          csv_id: csvId || '',
          source_type: 'gcs',
          source_url: '',
          ingestion_method: 'direct_upload',
        }

        const analysisId = await submitVideoAnalysis(submitPayload)

        // Prepare form data for this video
        const metadata = {
          Sports: getSelectedSport(),
          PlayerID: player_id ? player_id : (getPlayerIdFromUrl() || ''),
          UserID: user?.id,
          Query: analysisPrompt,
          AnalysisTitle: analysisTitle,
          VideoID: analysisId?.id,
          is_analysis: true,
          csv_id: csvId || '',
        }

        const getGSURL = await uploadFileToGCS(videoFile, metadata);
        await updateVideoAnalysis({
          id: analysisId?.id,
          gs_uri: getGSURL,
          csv_id: csvId || '',
        })
        return true
      } catch (error) {
        clearTimeout(timeoutId);
        console.error(`Error processing video ${videoFile.name}:`, error);
        throw error;
      }
    });

    // Wait for all analysis requests to complete
    const results = await Promise.allSettled(analysisPromises);

    // Process results - throw errors for any failed requests (no mock fallbacks)
    const processedResults = [];
    const failures = [];

    results.forEach((result, index) => {
      if (result.status === 'fulfilled') {
        processedResults.push(result.value);
      } else {
        console.error(`Analysis failed for video ${videoFiles[index].name}:`, result.reason);
        failures.push({ file: videoFiles[index].name, error: result.reason });
      }
    });

    // If any videos failed, throw an error with details
    if (failures.length > 0) {
      const failedFiles = failures.map(f => f.file).join(', ');
      throw new Error(`Analysis failed for ${failures.length} video(s): ${failedFiles}. Please try again.`);
    }

    return processedResults;
  } catch (error) {
    console.error('Error in multiple video analysis:', error);
    throw error;
  }
};

/**
 * Five-dimension pitcher analysis via Gemini 3 Flash agentic vision.
 * Calls POST /api/pitcher-analysis (Express proxy → slam-agents).
 * @param {Object} options
 * @param {string} options.youtubeUrl - YouTube video URL
 * @param {string} options.playerName - Pitcher name
 * @param {string} [options.playerId] - Player ID for storage linking
 * @param {number} [options.age=25] - Pitcher age (affects dimension 5)
 * @param {string[]} [options.dimensions] - Subset of dimensions (null = all 5)
 * @param {string} [options.customPrompt] - Additional analysis instructions
 * @param {string} [options.sport='MLB'] - Sport code
 * @returns {Promise<Object>} PitcherAnalysisResponse
 */
export const analyzePitcher = async ({ youtubeUrl, playerName, playerId, age, dimensions, customPrompt, sport = 'MLB' }) => {
  try {
    const payload = {
      youtube_url: youtubeUrl.startsWith('http') ? youtubeUrl : `https://${youtubeUrl}`,
      player_name: playerName,
      player_id: playerId || null,
      age: age || 25,
      sport,
    };
    if (dimensions && dimensions.length > 0 && dimensions.length < 5) {
      payload.dimensions = dimensions;
    }
    if (customPrompt?.trim()) {
      payload.custom_prompt = customPrompt.trim();
    }

    // Pitcher analysis endpoint is on the Node.js backend (same-origin)
    const response = await axios.post('/api/pitcher-analysis', payload, {
      timeout: 180000, // 3 minute timeout for video analysis
    });
    return response.data;
  } catch (error) {
    console.error('Pitcher analysis error:', error);
    const msg = error.response?.data?.error || error.response?.data?.detail || error.message;
    throw new Error(msg || 'Pitcher analysis failed');
  }
};

/**
 * Search YouTube for athlete videos
 * @param {Object} options
 * @param {string} options.query - Athlete name or search term
 * @param {number} [options.maxResults=8] - Max results (1-20)
 * @param {string} [options.sport] - Sport context for better results
 * @returns {Promise<Array>} Array of { videoId, title, thumbnail, channelTitle, publishedAt, url }
 */
export const searchYouTubeVideos = async ({ query, maxResults = 8, sport }) => {
  try {
    // YouTube search endpoint lives on the Node.js backend (same-origin),
    // NOT the Python backend/API gateway. Use axios with relative URL.
    const response = await axios.post('/api/youtube/search', {
      query,
      maxResults,
      sport,
    });
    return response.data?.results || [];
  } catch (error) {
    console.error('YouTube search error:', error);
    const msg = error.response?.data?.error || error.message;
    throw new Error(msg || 'YouTube search failed');
  }
};

/**
 * Multi-video YouTube analysis — sends multiple YouTube URLs for combined analysis.
 * @param {Object} options
 * @param {string[]} options.youtubeUrls - Array of YouTube video URLs
 * @param {string} options.playerName - Player/athlete name
 * @param {number} [options.age=25] - Player age
 * @param {string[]} [options.dimensions] - Subset of dimensions (pitcher mode)
 * @param {string} [options.customPrompt] - Additional analysis instructions
 * @param {string} [options.sport='MLB'] - Sport code
 * @returns {Promise<Object>} Analysis response
 */
export const analyzeMultipleYouTubeVideos = async ({ youtubeUrls, playerName, age, dimensions, customPrompt, sport = 'MLB' }) => {
  try {
    const payload = {
      youtube_urls: youtubeUrls.map(u => u.startsWith('http') ? u : `https://${u}`),
      player_name: playerName,
      age: age || 25,
      sport,
    };
    if (dimensions && dimensions.length > 0 && dimensions.length < 5) {
      payload.dimensions = dimensions;
    }
    if (customPrompt?.trim()) {
      payload.custom_prompt = customPrompt.trim();
    }

    // Multi-video analysis endpoint is on the Node.js backend (same-origin),
    // NOT the Python backend/API gateway. Use axios with relative URL.
    const response = await axios.post('/api/multi-video-analysis', payload, {
      timeout: 300000, // 5 minute timeout for multi-video
    });
    return response.data;
  } catch (error) {
    console.error('Multi-video analysis error:', error);
    const msg = error.response?.data?.error || error.response?.data?.detail || error.message;
    throw new Error(msg || 'Multi-video analysis failed');
  }
};

export default {
  analyzeMultipleVideos,
  analyzeMultipleYouTubeVideos,
  analyzePitcher,
  analyzeVideo,
  generateAudio,
  generatePDF,
  getAllVideoAnalytics,
  getVideoAnalyticsById,
  getVideoAnalyticsByPlayerId,
  saveVideoAnalytics,
  searchYouTubeVideos,
  updateVideoAnalytics,
  deleteVideoAnalytics
};
